import { useQuery } from "@apollo/client";
import { FaClipboardList, FaCrown, FaUser } from "react-icons/fa";
import { useAuth } from "../../../context/AuthProvider";
import { useProfiles } from "../../../context/ProfilesProvider";
import { GET_CB_TIPPER_PROFILE_DETAILS } from "../../../queries/mutation/cb/cb_queries";
import { GET_MY_TIP_QUEUE } from "../../../queries/tipQueue";
import { TipQueueEntry } from "../../../types/TipQueue";
import LoadingPage from "../../LoadingPage";
import { EntryCard } from "../../tipQueue/EntryCard";
import StatTile from "../broadcasters/community/StatTile";
import { formatDate } from "../broadcasters/community/format";
import DashCard from "../dashboard/DashCard";

// A tipper's view of their own Chaturbate side of things: who they are
// here, their numbers, what's waiting or saved for them on the queue, and
// what they've tipped.
const TipperProfile = () => {
  const { user } = useAuth();
  const { cbProfile, cbLoading } = useProfiles();
  const { loading, data } = useQuery(GET_CB_TIPPER_PROFILE_DETAILS);
  const { data: queueData } = useQuery(GET_MY_TIP_QUEUE, {
    fetchPolicy: "cache-and-network",
    pollInterval: 15000,
  });

  if (cbLoading || (loading && !data)) {
    return <LoadingPage />;
  }
  if (!cbProfile) {
    return (
      <div className="bg-base-200 min-h-full p-4 text-center text-base-content/60">
        Link your Chaturbate account on Link Accounts to see this page.
      </div>
    );
  }

  const counts: { amount: number; count: number }[] =
    data?.getCBTipperProfileDetails ?? [];
  const totalTips = counts.reduce((sum, c) => sum + c.amount * c.count, 0);
  const timesTipped = counts.reduce((sum, c) => sum + c.count, 0);
  const maxCount = Math.max(1, ...counts.map((c) => c.count));
  const waiting: TipQueueEntry[] = queueData?.getMyTipQueue?.waiting ?? [];
  const banked: TipQueueEntry[] = queueData?.getMyTipQueue?.banked ?? [];
  const broadcaster = user?.broadcaster ?? "";
  const joinFanclub = `https://chaturbate.com/fanclub/join/${broadcaster}/?source=SupporterSourceJoinFanClubButton`;

  return (
    <div className="bg-base-200 min-h-full p-2 sm:p-4 flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex items-center gap-3">
          <FaUser size={28} className="text-warning shrink-0" />
          <div>
            <div className="text-xs uppercase tracking-wide text-base-content/60">
              Your Chaturbate profile
            </div>
            <h2 className="text-2xl font-bold text-warning break-all leading-tight">
              {cbProfile.username}
            </h2>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {cbProfile.inFanclub ? (
            <span className="badge badge-success gap-1">
              <FaCrown /> fan club
            </span>
          ) : (
            <a
              href={joinFanclub}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-success btn-sm"
            >
              Join the fan club
            </a>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <StatTile label="Tokens tipped" value={totalTips} className="text-primary" />
        <StatTile
          label="Biggest tip"
          value={cbProfile.lifetimeBiggestTip}
          className="text-primary"
        />
        <StatTile label="Times tipped" value={timesTipped} />
        <StatTile
          label="Fan club"
          value={cbProfile.inFanclub ? "Member" : "Not yet"}
          hint={
            cbProfile.createdDatetime
              ? `on CB since ${formatDate(cbProfile.createdDatetime)}`
              : undefined
          }
          className={cbProfile.inFanclub ? "text-success" : ""}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <DashCard
          title={
            <>
              <FaClipboardList className="text-accent" /> Saved for later
              <span className="text-base-content/60 font-normal">({banked.length})</span>
            </>
          }
        >
          {banked.length === 0 ? (
            <div className="text-sm text-base-content/60">
              Nothing saved for later. When a tip of yours is set aside to be
              done another time, it shows here until it's done.
            </div>
          ) : (
            <ul className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-1">
              {banked.map((entry, index) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  lane="banked"
                  index={index}
                  count={banked.length}
                  readOnly
                />
              ))}
            </ul>
          )}
        </DashCard>

        <DashCard
          title={
            <>
              Waiting on stream
              <span className="text-base-content/60 font-normal">({waiting.length})</span>
            </>
          }
        >
          {waiting.length === 0 ? (
            <div className="text-sm text-base-content/60">
              Nothing waiting right now. Tips that buy a menu item show here
              until they're done on stream.
            </div>
          ) : (
            <ul className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-1">
              {waiting.map((entry, index) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  lane="waiting"
                  index={index}
                  count={waiting.length}
                  readOnly
                />
              ))}
            </ul>
          )}
        </DashCard>

        <DashCard title="What you've tipped" className="md:col-span-2">
          {counts.length === 0 ? (
            <div className="text-sm text-base-content/60">No tips recorded yet.</div>
          ) : (
            <ul className="flex flex-col gap-1 max-h-96 overflow-y-auto pr-1">
              {[...counts]
                .sort((a, b) => a.amount - b.amount)
                .map((c) => (
                  <li key={c.amount} className="flex items-center gap-2 text-sm">
                    <span className="w-16 shrink-0 font-semibold text-primary text-right">
                      {c.amount.toLocaleString()}
                    </span>
                    <span className="flex-1 h-3 bg-base-300 rounded overflow-hidden">
                      <span
                        className="block h-full bg-primary/70"
                        style={{ width: `${Math.max(4, (c.count / maxCount) * 100)}%` }}
                      />
                    </span>
                    <span className="w-12 shrink-0 text-right">{c.count}×</span>
                  </li>
                ))}
            </ul>
          )}
        </DashCard>
      </div>
    </div>
  );
};

export default TipperProfile;
